import { useEffect, useState } from "react";
import { doc, getFirestore } from "firebase/firestore";
import {
  getAuth,
  onAuthStateChanged,
  deleteUser,
  signOut,
  reauthenticateWithPopup,
  reauthenticateWithCredential,
  GoogleAuthProvider,
  EmailAuthProvider,
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { isAccountValid } from "helpers/is-account-valid";
import { UserKeys } from 'fixit-shared/FirestoreKeys';
import { CurrentAccountVersion } from 'fixit-shared/Consts';
import { useCachedDoc } from 'hooks/useCache';

const MIGRATE_ACCOUNT_FUNCTION = 'migrateAccount';

const useUserSnapshot = (uid) => {
  const userPath = uid ? `${UserKeys.tableName}/${uid}` : undefined;
  return useCachedDoc(userPath, UserKeys.decompress);
}

export const getUserRef = (uid) => {
  return doc(getFirestore(), "user", uid);
};

export const deleteAccount = async (password) => {
  const user = getAuth().currentUser;
  let reauth = undefined;

  if(user.providerData[0].providerId === GoogleAuthProvider.PROVIDER_ID){
    var provider = new GoogleAuthProvider();
    reauth = reauthenticateWithPopup(user, provider);
  }
  else if(user.providerData[0].providerId === EmailAuthProvider.PROVIDER_ID){
    const credential = EmailAuthProvider.credential(
        user.email,
        password
    );
    reauth = reauthenticateWithCredential(user, credential);
  }

  if(reauth){
    return reauth.then(() => {
      return deleteUser(getAuth().currentUser);
    })
    .then(() => signOut(getAuth()))
    .then(() => {
      return {success: true}
    }).catch(function(error) {
      return {success: false, error: error.message};
    });
  }

  return {success: false, error: 'Could not recognise identity provider. Contact support@ifixitapps.com to request deletion manually.'};
}

export const useCurrentUserSnapshot = () => {
  const [uid, setUid] = useState();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(getAuth(), (user) => {
      setUid(user ? user.uid : null);
    });

    return () => unsubscribe();
  }, []);

  return useUserSnapshot(uid);
};

export const needsAccountMigration = (user) => {
  if (!user) return false;

  // A comparison against a missing constant is false, which would silently fail. That is not
  // hypothetical: fixit-shared is pre-bundled by Vite, so a dev server holding a
  // stale copy of it produces exactly this and looks like the feature simply not
  // working. Ask the server instead.
  if (typeof CurrentAccountVersion !== 'number') {
    console.warn('CurrentAccountVersion is missing from fixit-shared/Consts; migrating on the server\'s judgement instead');
    return true;
  }

  const version = typeof user.schemaVersion === 'number' ? user.schemaVersion : 0;

  return version < CurrentAccountVersion;
};

export const migrateAccount = (cloudFunctions) => {
  const migrate = httpsCallable(cloudFunctions, MIGRATE_ACCOUNT_FUNCTION);
  return migrate();
};

// Brings the signed-in account up to date on sign-in. The snapshot
// already carries the account's version so is free to check and means an
// account that is current never reaches the server at all
export const useAccountMigration = (cloudFunctions) => {
  const user = useCurrentUserSnapshot();
  const isBehind = needsAccountMigration(user);

  useEffect(() => {
    if (!cloudFunctions || !isBehind) return;

    // Failures are logged and dropped rather than surfaced. The next sign-in
    // tries again, and an account that cannot be migrated keeps working with
    // what it has — neither is a reason to put an error in front of someone who
    // has just signed in yet.
    migrateAccount(cloudFunctions)
      .catch(error => console.error(`${MIGRATE_ACCOUNT_FUNCTION} failed:\n${error}`));
  }, [cloudFunctions, isBehind]);
};

export const useIsCurrentUserValid = () => {
  const user = useCurrentUserSnapshot();
  return user && isAccountValid(user).valid;
};

export const useLoggedInState = (authService, onSignInCallback, onSignOutCallback) => {
  const isUserValid = useIsCurrentUserValid();
  const [isSignedIn, setIsSignedIn] = useState(isUserValid);  
  
  useEffect(() => {

    setIsSignedIn(isUserValid)

    const onSignIn = (_user) => {
        setIsSignedIn(true);
        if(onSignInCallback) onSignInCallback();
    }

    const onSignOut = () => {
        setIsSignedIn(false);
        if(onSignOutCallback) onSignOutCallback();
    }

    authService.addAuthCallbacks(onSignIn, onSignOut);
    return () => {
        authService.removeAuthCallbacks(onSignIn, onSignOut);
    }
  }, [authService, isUserValid, onSignInCallback, onSignOutCallback]);

  return isSignedIn;
}